import { CHART_REGISTRY, type ChartName } from '@revelio/replots';

import type { ChartDataEntry } from './SharedReportDataContext';

interface StaticChartRendererProps {
  chartDataEntry: ChartDataEntry;
}

// Alias table for historical chart-type strings captured by older share
// snapshots before the replots registry became the single source of truth.
// Everything here maps to a current CHART_REGISTRY key; new shares use the
// registry keys directly and don't need an alias.
const LEGACY_NAME_ALIASES: Record<string, ChartName> = {
  KDE: 'AreaChart',
  Sankey: 'SankeyChart',
  ReplotsBarChart: 'BarChart',
  ReplotsLineChart: 'LineChart',
  ReplotsStackedBarChartHorizontal: 'StackedBarChartHorizontal',
  ReplotsGroupedBarChartHorizontal: 'GroupedBarChart',
  ReplotsDivergingBarChart: 'DivergingBarChart',
  ReplotsVolumeChart: 'VolumeChart',
  ReplotsBoxPlot: 'BoxPlot',
  ReplotsKDE: 'AreaChart',
  ReplotsAreaChart: 'AreaChart',
  ReplotsSankeyChart: 'SankeyChart',
  ReplotsTreeMap: 'TreeMap',
};

const resolveChart = (name: string) => {
  if (name in CHART_REGISTRY) return CHART_REGISTRY[name as ChartName];
  const aliased = LEGACY_NAME_ALIASES[name];
  return aliased ? CHART_REGISTRY[aliased] : undefined;
};

export const StaticChartRenderer = ({
  chartDataEntry,
}: StaticChartRendererProps) => {
  if (!chartDataEntry.component) return null;
  const Component = resolveChart(chartDataEntry.component);
  if (!Component) return null;
  const SafeComponent = Component as React.ComponentType<
    Record<string, unknown>
  >;
  return <SafeComponent {...(chartDataEntry.props ?? {})} />;
};
